import { observer } from 'mobx-react-lite';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Icon, IconArrowBigLeft, IconArrowBigRight } from '@flixbus/honeycomb-icons-react';

import CurrentTimeIndicator from './components/CurrentTimeIndicator';
import MidnightIndicator from './components/MidnightIndicator';
import TimelineRow from './components/TimelineRow';
import TimeScale from './components/TimeScale';
import { calculateActivityPosition } from './utils/calculateActivityPosition';

import styles from './styles.module.css';

const buildPositionCacheKey = (item, visibleDateRange, timezone) => {
	const rangeStart = visibleDateRange?.[0]?.date ?? '';
	const rangeEnd = visibleDateRange?.[visibleDateRange.length - 1]?.date ?? '';
	const itemId = item?.id ?? item?.key ?? `${item?.fromTimestamp}-${item?.toTimestamp}`;

	return `${itemId}__${item?.fromTimestamp}__${item?.toTimestamp}__${timezone}__${rangeStart}__${rangeEnd}__${visibleDateRange?.length ?? 0}`;
};

const getCachedPosition = (cache, item, visibleDateRange, timezone) => {
	const cacheKey = buildPositionCacheKey(item, visibleDateRange, timezone);

	if (cache.has(cacheKey)) {
		return cache.get(cacheKey);
	}

	const position = calculateActivityPosition(item, visibleDateRange, timezone);
	cache.set(cacheKey, position);

	return position;
};

const TimelineGrid = observer(
	({
		drivers,
		dateRange,
		timezone,
		displayDays,
		onPreviousPeriod,
		onNextPeriod,
		canNavigatePrevious = true,
		canNavigateNext = true,
	}) => {
		const [activeItem, setActiveItem] = useState(null);

		const handleSetActiveItem = useCallback((item) => {
			setActiveItem(item);
		}, []);

		const handleClearActiveItem = useCallback((expectedItemId = null) => {
			setActiveItem((currentActiveItem) => {
				if (expectedItemId && currentActiveItem?.id !== expectedItemId) {
					return currentActiveItem;
				}

				return null;
			});
		}, []);

		const visibleDateRange = dateRange.slice(0, displayDays || dateRange.length);
		const prevEnabled = canNavigatePrevious && onPreviousPeriod;
		const nextEnabled = canNavigateNext && onNextPeriod;
		const daysCount = visibleDateRange.length;

		const positionCacheRef = useRef(new Map());
		const lastPositionCacheScopeRef = useRef('');

		const positionCacheScope = useMemo(() => {
			const rangeStart = visibleDateRange?.[0]?.date ?? '';
			const rangeEnd = visibleDateRange?.[visibleDateRange.length - 1]?.date ?? '';
			return `${timezone}__${rangeStart}__${rangeEnd}__${visibleDateRange.length}`;
		}, [timezone, visibleDateRange]);

		if (lastPositionCacheScopeRef.current !== positionCacheScope) {
			positionCacheRef.current.clear();
			lastPositionCacheScopeRef.current = positionCacheScope;
		}

		return (
			<div className={styles.timelineGrid}>
				<div className={styles.headerRow}>
					{visibleDateRange.map((date, index) => {
						const isFirst = index === 0;
						const isLast = index === visibleDateRange.length - 1;

						return (
							<div key={date.date} className={styles.dateColumn}>
								{!isFirst && <div className={styles.midnightLine} />}
								<div className={styles.dateHeader}>
									{isFirst && onPreviousPeriod ? (
										<div
											className={styles.iconArrowRadius}
											onClick={prevEnabled ? onPreviousPeriod : undefined}
											role="button"
											aria-label="Previous period"
											aria-disabled={!prevEnabled}
											title={!prevEnabled ? 'Date is before allowed range' : undefined}
										>
											<Icon
												InlineIcon={IconArrowBigLeft}
												extraClasses={prevEnabled ? styles.navArrow : styles.navArrowDisabled}
												size={3}
											/>
										</div>
									) : (
										<div />
									)}

									<div
										className={`${styles.dateContent} ${
											moment().format('MMM DD') === date.date ? styles.currentDate : ''
										}`}
									>
										<span className={styles.weekday}>{date.weekday}</span>
										<span className={styles.exact}>{date.date}</span>
									</div>

									{isLast && onNextPeriod ? (
										<div
											className={styles.iconArrowRadius}
											onClick={nextEnabled ? onNextPeriod : undefined}
											role="button"
											aria-label="Next period"
											aria-disabled={!nextEnabled}
											title={!nextEnabled ? 'Date is after allowed range' : undefined}
										>
											<Icon
												InlineIcon={IconArrowBigRight}
												extraClasses={nextEnabled ? styles.navArrow : styles.navArrowDisabled}
												size={3}
											/>
										</div>
									) : (
										<div />
									)}
								</div>

								<TimeScale isLastLabel={isLast} isFirstColumn={isFirst} />
							</div>
						);
					})}
				</div>

				<div className={styles.contentWrapper}>
					<MidnightIndicator dateRange={visibleDateRange} timezone={timezone} />
					<CurrentTimeIndicator dateRange={visibleDateRange} timezone={timezone} />

					{drivers.map((driver) => {
						const rowActiveItem = activeItem?.crewMemberId === driver.id ? activeItem : null;
						const rowActiveItemId = rowActiveItem?.id ?? null;

						return (
							<TimelineRow
								key={driver.id}
								driver={driver}
								visibleDateRange={visibleDateRange}
								timezone={timezone}
								daysCount={daysCount}
								rowActiveItem={rowActiveItem}
								rowActiveItemId={rowActiveItemId}
								onSetActiveItem={handleSetActiveItem}
								onClearActiveItem={handleClearActiveItem}
								getCachedPosition={(item, range, tz) =>
									getCachedPosition(positionCacheRef.current, item, range, tz)
								}
							/>
						);
					})}
				</div>
			</div>
		);
	}
);

export default TimelineGrid;
